/**
 * useProjectTreeDialogs Hook
 * 
 * Manages all dialog/modal visibility states and their associated data for the ProjectTree component.
 * Consolidates dialog state management to reduce component complexity.
 * 
 * Purpose: Centralize all dialog state in one hook
 * Usage: const { dialogs, actions } = useProjectTreeDialogs();
 */

import { useState, useCallback, useMemo } from "react";

export function useProjectTreeDialogs() {
  // Settings dialog
  const [showSettings, setShowSettings] = useState(false);
  
  // Project/Year/Task/Sequence creation dialogs
  const [showAddProject, setShowAddProject] = useState(false);
  const [showAddYear, setShowAddYear] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);
  const [showAddSequence, setShowAddSequence] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");
  const [initialDropFiles, setInitialDropFiles] = useState<File[]>([]);
  const [selectedYearId, setSelectedYearId] = useState<string>("");
  const [selectedSequenceProjectId, setSelectedSequenceProjectId] = useState<string>("");
  
  // Chat dialog
  const [showChatDialog, setShowChatDialog] = useState(false);
  const [selectedChatProjectId, setSelectedChatProjectId] = useState<string>("");
  const [currentChatPulse, setCurrentChatPulse] = useState<any>(null);
  const [chatTargetTimestamp, setChatTargetTimestamp] = useState<Date | undefined>();
  
  // Mail out dialog
  const [showMailOutDialog, setShowMailOutDialog] = useState(false);
  const [selectedMailOutProjectId, setSelectedMailOutProjectId] = useState<string>("");
  
  // Milestone dialog
  const [showMilestoneDialog, setShowMilestoneDialog] = useState(false);
  const [selectedMilestoneProjectId, setSelectedMilestoneProjectId] = useState<string>("");
  
  // Time entry popup
  const [showTimePopup, setShowTimePopup] = useState(false);
  const [selectedTimeProjectId, setSelectedTimeProjectId] = useState<string>("");
  const [selectedTimePulseId, setSelectedTimePulseId] = useState<number>(0);
  const [timeMousePosition, setTimeMousePosition] = useState({ x: 0, y: 0 });
  
  // Action creators for opening dialogs with context
  const openAddProjectDialog = useCallback((yearId: string) => {
    setSelectedYearId(yearId);
    setShowAddProject(true);
  }, []);
  
  const openAddSequenceDialog = useCallback((projectId: string) => {
    setSelectedSequenceProjectId(projectId);
    setShowAddSequence(true);
  }, []);
  
  const openChatDialog = useCallback((projectId: string, pulse?: any, targetTimestamp?: Date) => {
    setSelectedChatProjectId(projectId);
    setCurrentChatPulse(pulse || null);
    setChatTargetTimestamp(targetTimestamp);
    setShowChatDialog(true);
  }, []);
  
  const openMailOutDialog = useCallback((projectId: string) => {
    setSelectedMailOutProjectId(projectId);
    setShowMailOutDialog(true);
  }, []);
  
  const openMilestoneDialog = useCallback((projectId: string) => {
    setSelectedMilestoneProjectId(projectId);
    setShowMilestoneDialog(true);
  }, []);
  
  const openTimePopup = useCallback((projectId: string, pulseId: number, mousePosition: { x: number, y: number }) => {
    setSelectedTimeProjectId(projectId);
    setSelectedTimePulseId(pulseId);
    setTimeMousePosition(mousePosition);
    setShowTimePopup(true);
  }, []);

  const openAddTaskWithFiles = useCallback((projectId: string, files: File[]) => {
    setSelectedProjectId(projectId);
    setInitialDropFiles(files);
    setShowAddTask(true);
  }, []);

  // Memoize actions object to prevent recreation on every render
  // All functions inside are stable (setState from useState, useCallback with [])
  const actions = useMemo(() => ({
    // Simple setters
    setShowSettings,
    setShowAddProject,
    setShowAddYear,
    setShowAddTask,
    setShowAddSequence,
    setShowChatDialog,
    setShowMailOutDialog,
    setShowMilestoneDialog,
    setShowTimePopup,
    setSelectedProjectId,
    setInitialDropFiles,
    // Contextual openers
    openAddProjectDialog,
    openAddSequenceDialog,
    openAddTaskWithFiles,
    openChatDialog,
    openMailOutDialog,
    openMilestoneDialog,
    openTimePopup
  }), [
    openAddProjectDialog,
    openAddSequenceDialog,
    openAddTaskWithFiles,
    openChatDialog,
    openMailOutDialog,
    openMilestoneDialog,
    openTimePopup
  ]);

  return {
    // Dialog visibility states
    dialogs: {
      settings: showSettings,
      addProject: showAddProject,
      addYear: showAddYear,
      addTask: showAddTask,
      addSequence: showAddSequence,
      chat: showChatDialog,
      mailOut: showMailOutDialog,
      milestone: showMilestoneDialog,
      timePopup: showTimePopup
    },
    // Dialog context data
    context: {
      selectedProjectId,
      selectedYearId,
      selectedSequenceProjectId,
      selectedChatProjectId,
      currentChatPulse,
      chatTargetTimestamp,
      selectedMailOutProjectId,
      selectedMilestoneProjectId,
      selectedTimeProjectId,
      selectedTimePulseId,
      timeMousePosition,
      initialDropFiles
    },
    // Actions (memoized for callback stability)
    actions,
    // Direct export of openTimePopup for optimal callback stability
    // (avoids dependency on entire actions object)
    openTimePopup
  };
}
